import React, { useState } from 'react';
import {
  Flex,
  Div,
  Gutter,
  Title,
  BodyText,
  Table,
  GridBag,
  Checkbox,
  OverflowMenu,
  ModalDialog,
  Dropdown,
  Button,
  Icon,
} from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { AdminProductVariant, LocalProperty, UrlImage } from '@cdaas/api-clients/dist/product-service-openapi';

import { AdminProductVariantWithKey } from '../../../../utils/types';
import { getInitialVariant } from '../../../../utils/products';
import { updateEntityByIndex } from '../../../../utils/entities';
import { Errors } from '../utils';
import VariantDetail from './VariantDetail';

import './VariantsDetail.scss';

interface VariantsDetailProps {
  title: string;
  allPropertiesInSystem: LocalProperty[];
  variants: AdminProductVariant[] | undefined;
  properties: LocalProperty[] | undefined;
  handleVariantsChange: (variants: AdminProductVariant[]) => void;
  handleAddProperty: (property: LocalProperty) => void;
  handleRemoveProperty: (property: LocalProperty) => void;
  brand: string | undefined;
  images: UrlImage[] | undefined;
  handleImagesChange: (images: UrlImage[]) => void;
  errors: Errors['variants'];
}

function VariantsDetail(props: VariantsDetailProps) {
  const {
    title,
    allPropertiesInSystem,
    variants,
    properties,
    handleVariantsChange,
    handleAddProperty,
    handleRemoveProperty,
    brand,
    images,
    handleImagesChange,
    errors,
  } = props;
  const { t } = useTranslation('productscreen');

  const [selectedVariants, setSelectedVariants] = useState<(number | string)[]>([]);

  const [showEditVariantOverflowMenu, setShowEditVariantOverflowMenu] = useState<boolean>(false);
  const [showEditVariantTypesModal, setShowEditVariantTypesModal] = useState<boolean>(false);

  const [showAddNewVariantPropertyInput, setShowAddNewVariantPropertyInput] = useState<boolean>(false);
  const [currentlyChosenNewVariantProperty, setCurrentlyChosenNewVariantProperty] = useState<LocalProperty>();

  const checkAllVariantsSelected = () => {
    return variants && Object.values(variants).length === selectedVariants.length;
  };
  const handleSelectAllVariants = () => {
    setSelectedVariants((variants || []).map((variant: AdminProductVariantWithKey) => variant.id || (variant.key as string)));
  };
  const handleDeselectAllVariants = () => {
    setSelectedVariants([]);
  };
  const handleSelectVariant = (variantIdOrKey: number | string) => {
    setSelectedVariants([...selectedVariants, variantIdOrKey]);
  };
  const handleDeselectVariant = (variantIdOrKey: number | string) => {
    const updatedSelectedVariants = selectedVariants.filter(selectedVariantIdOrKey => selectedVariantIdOrKey !== variantIdOrKey);
    setSelectedVariants(updatedSelectedVariants);
  };

  const handleCurrentlyChosenNewVariantPropertyChange = (propertyId: number) => {
    const property = allPropertiesInSystem.find(property => property.id === propertyId);
    setCurrentlyChosenNewVariantProperty(property);
  };

  const handleNewVariantPropertyAdd = (property: LocalProperty | undefined) => {
    if (property) {
      handleAddProperty(property);
    }
    setShowAddNewVariantPropertyInput(false);
    setCurrentlyChosenNewVariantProperty(undefined);
  };

  const handleVariantPropertyRemove = (propertyId: number) => {
    const propertyToRemove = allPropertiesInSystem.find(property => property.id === propertyId);
    if (propertyToRemove) {
      handleRemoveProperty(propertyToRemove);
    }
  };

  const handleNewVariantAdd = () => {
    const variant = getInitialVariant();
    // If product already has variants with TFIDs or TFAPs set for some country, those same TFID and TFAP values
    // should be set for the new variant as well
    if (variants && variants?.length > 0) {
      // Currently all variants of a product should have same TFIDs and TFAPs per country
      const someVariant = variants[0];
      const existingFinanceProductIds = someVariant.finance_product_ids.map(({ country_id, finance_product_id }) => ({
        country_id,
        finance_product_id,
        id: 0,
        variant_id: variant.id,
      }));
      const existingAcquisitionPrices = someVariant.country_acquisition_prices.map(({ country_id, amount }) => ({
        country_id,
        amount,
        id: 0,
        variant_id: variant.id,
      }));
      variant.finance_product_ids = existingFinanceProductIds;
      variant.country_acquisition_prices = existingAcquisitionPrices;
    }
    handleVariantsChange([...(variants || []), variant]);
  };

  const handleVariantUpdate = (updatedVariant: AdminProductVariantWithKey) => {
    const index = (variants || []).findIndex((variant: AdminProductVariantWithKey) => {
      return (variant.id && variant.id === updatedVariant.id) || (variant.key && variant.key === updatedVariant.key);
    });
    if (index !== -1) {
      const updatedVariants = updateEntityByIndex(variants, index, updatedVariant);
      handleVariantsChange(updatedVariants);
    }
  };

  const handleSelectedVariantsDeletion = () => {
    if (variants) {
      const updatedVariants = variants.filter(
        (variant: AdminProductVariantWithKey) => !selectedVariants.includes(variant.id || (variant.key as string)),
      );
      handleVariantsChange(updatedVariants);
      handleDeselectAllVariants();
    }
  };

  const propertyHeaders = properties ? properties.map(property => property.name) : [];
  const headers = ['', ...propertyHeaders, t('variants_detail_section.sku_table_header'), ''];

  return (
    <Div>
      {/* Product variations table */}
      <Flex directionColumn>
        <Title size="md" padding={{ right: 'lg' }}>
          {title}
        </Title>
        <Gutter />
        <BodyText color="gray400">{t('variants_detail_section.info')}</BodyText>
        <Gutter />
        {selectedVariants.length > 0 && (
          <Div>
            <GridBag>
              <GridBag.Item md={6}>
                <BodyText fontWeight="bold">{t('variants_detail_section.selected_variants_count', { count: selectedVariants.length })}</BodyText>
              </GridBag.Item>
              <GridBag.Item md={4} alignTextToRight={true}>
                <Button
                  variant="destructive"
                  onClick={() => handleSelectedVariantsDeletion()}
                  size="md"
                  disabled={checkAllVariantsSelected()}
                  title={checkAllVariantsSelected() ? t('variants_detail_section.cant_delete_all_variants_tooltip') : ''}
                >
                  {t('variants_detail_section.buttons.delete_variants')}
                </Button>
              </GridBag.Item>
            </GridBag>
            <Gutter size="sm" />
          </Div>
        )}
        <GridBag>
          {/* Adjust the size of the table based on the number of columns */}
          <GridBag.Item md={Math.min(headers.length * 2, 12)}>
            <Table className="product-variants-details__variants-table">
              <thead>
                <Table.Row>
                  {headers.map((header, index) => {
                    if (index === 0) {
                      return (
                        <Table.HeaderCell key={`${index}-${header}`}>
                          <Checkbox
                            checked={checkAllVariantsSelected()}
                            onChange={() => (checkAllVariantsSelected() ? handleDeselectAllVariants() : handleSelectAllVariants())}
                          />
                        </Table.HeaderCell>
                      );
                    }
                    if (index === headers.length - 1) {
                      return (
                        <Table.HeaderCell key={`${index}-${header}`}>
                          <Div alignTextToRight>
                            <OverflowMenu
                              data-testid="variant-properties-overflow-menu"
                              onClick={() => setShowEditVariantOverflowMenu(!showEditVariantOverflowMenu)}
                              open={showEditVariantOverflowMenu}
                            >
                              <OverflowMenu.Item
                                onClick={() => setShowEditVariantTypesModal(true)}
                                label={t('variants_detail_section.overflow_menu_edit_variant_types')}
                              />
                              <OverflowMenu.Item onClick={() => undefined} label={t('variants_detail_section.overflow_manu_reset_variant_types')} />
                            </OverflowMenu>
                          </Div>
                        </Table.HeaderCell>
                      );
                    }
                    return (
                      <Table.HeaderCell key={`${index}-${header}`}>
                        <Div alignTextToCenter>
                          <BodyText fontWeight="bold">{header}</BodyText>
                        </Div>
                      </Table.HeaderCell>
                    );
                  })}
                </Table.Row>
              </thead>
              <tbody>
                {(variants || [])
                  .sort((a, b) => (b.id === 0 || a.id === 0 ? b.id - a.id : a.id - b.id))
                  .map((variant: AdminProductVariantWithKey, index) => (
                    <VariantDetail
                      key={index}
                      index={index}
                      variant={variant}
                      allPropertiesInSystem={allPropertiesInSystem}
                      properties={properties}
                      handleVariantUpdate={handleVariantUpdate}
                      selected={selectedVariants.includes(variant.id || (variant.key as string))}
                      handleSelectVariant={handleSelectVariant}
                      handleDeselectVariant={handleDeselectVariant}
                      brand={brand}
                      images={images}
                      handleImagesChange={handleImagesChange}
                      errors={{
                        variantSKU: errors?.variantsSKUs?.find(variantSKUError => variantSKUError.index === index)?.error,
                        propertyValues: errors?.variantsPropertyValues?.find(propertyValueError => propertyValueError.index === index)?.errors,
                        duplicate: errors?.variantsDuplicates?.find(duplicateError => duplicateError.index === index)?.error,
                      }}
                    />
                  ))}
              </tbody>
            </Table>
          </GridBag.Item>
        </GridBag>
        <Gutter size="sm" />
        <Flex flexItem occupyHorizontalSpace={false}>
          <BodyText margin={{ right: 'md' }} clickableAppearance={true} color="purple500" onClick={() => handleNewVariantAdd()}>
            {`+ ${t('variants_detail_section.add_new_variant')}`}
          </BodyText>
        </Flex>
      </Flex>

      {/* Add applicable properties to product -modal */}
      <ModalDialog isOpen={showEditVariantTypesModal} onCloseClick={() => setShowEditVariantTypesModal(false)} title="">
        <Flex directionColumn>
          <Title size="md">{t('variants_detail_section.variant_types_modal_title')}</Title>
          <Gutter />
          <BodyText color="gray400">{t('variants_detail_section.variant_types_modal_info')}</BodyText>
          <Gutter />
          <Table className="product-variants-details__variants-types-table">
            <thead>
              <Table.Row>
                <Table.HeaderCell>
                  <BodyText fontWeight="bold">{t('variants_detail_section.variant_types_variant_type_table_header')}</BodyText>
                </Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </thead>
            <tbody>
              {(properties || []).map((property, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <BodyText>{property.name}</BodyText>
                  </Table.Cell>
                  <Table.Cell>
                    <Icon
                      data-testid={`delete-variant-type-property-id-${property.id}`}
                      title={t('variants_detail_section.delete_variant_type')}
                      onClick={() => handleVariantPropertyRemove(property.id)}
                      clickableAppearance={true}
                      size="md"
                      color="gray400"
                      name="delete"
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </tbody>
          </Table>
          <Gutter />
          <Flex
            centeredVertically
            className="product-variants-detail__add_new_variant-type-flex"
            onClick={() => setShowAddNewVariantPropertyInput(!showAddNewVariantPropertyInput)}
          >
            <BodyText clickableAppearance={true} color="purple500">
              {`+ ${t('variants_detail_section.variant_types_modal_add_variant_type')}`}
            </BodyText>
            <Icon
              data-testid="open-add-variant-type-div"
              color="purple500"
              clickableAppearance={true}
              size="sm"
              margin={{ left: 'xs' }}
              name={showAddNewVariantPropertyInput ? 'arrow-up' : 'arrow-down'}
            />
          </Flex>
          <Div style={!showAddNewVariantPropertyInput ? { display: 'none' } : undefined}>
            <Gutter size="sm" />
            <Dropdown
              name="add-new-property-type-applicable-to-product-variants"
              label={t('variants_detail_section.add_variant_type_dropdown_label')}
              onChange={(_, option) => handleCurrentlyChosenNewVariantPropertyChange(Number(option.value))}
              selectedOption={
                currentlyChosenNewVariantProperty && {
                  label: currentlyChosenNewVariantProperty?.name,
                  value: currentlyChosenNewVariantProperty?.id.toString(),
                }
              }
              options={allPropertiesInSystem
                .filter(property => !(properties || []).find(alreadyChosenProperty => alreadyChosenProperty.id === property.id))
                .map(property => ({ label: property.name, value: property.id.toString() }))}
              disabled={allPropertiesInSystem.length === properties?.length}
              placeholder={
                allPropertiesInSystem.length !== properties?.length
                  ? t('variants_detail_section.add_variant_type_dropdown_placeholder')
                  : t('variants_detail_section.add_variant_type_dropdown_placeholder_all_properties_added')
              }
            />
            <Gutter size="sm" />
            <Button disabled={!currentlyChosenNewVariantProperty} onClick={() => handleNewVariantPropertyAdd(currentlyChosenNewVariantProperty)}>
              {t('variants_detail_section.buttons.add_variant_type')}
            </Button>
          </Div>
        </Flex>
      </ModalDialog>
    </Div>
  );
}

export default React.memo(VariantsDetail);
